import React, {FC, useEffect, useState} from 'react'
import {Link, useParams} from 'react-router-dom'
import '../public.css'
import {IPublicJobModel, optionLabel, PUBLIC_JOB_PATH, SECTOR_OPTIONS} from '../../hr/job/JobModel'
import {getPublicCompanyBySlug, getPublicJobsOfCompany} from '../PublicApi'
import {IPublicCompanyModel, publicDocumentUrl, safeExternalUrl} from '../PublicModels'
import {PublicBanner} from '../components/PublicBanner'
import {JobLocations, JobMeta} from '../components/JobMeta'
import {PublicLoading, PublicNotFound} from '../components/PublicState'

type LoadState = 'loading' | 'loaded' | 'notFound'

/** Firmanın kariyer sayfası (/company/:slug): tanıtım ve açık pozisyonlar */
const PublicCompanyPage: FC = () => {
  const {slug} = useParams<{slug: string}>()
  const [company, setCompany] = useState<IPublicCompanyModel | null>(null)
  const [jobs, setJobs] = useState<IPublicJobModel[]>([])
  const [state, setState] = useState<LoadState>('loading')

  useEffect(() => {
    setState('loading')
    getPublicCompanyBySlug(slug)
      .then(async ({data}) => {
        setCompany(data.payload)
        document.title = `${data.payload.name} · Kariyer`
        const jobsResponse = await getPublicJobsOfCompany(data.payload.id)
        setJobs(jobsResponse.data.payload || [])
        setState('loaded')
      })
      .catch(() => setState('notFound'))
  }, [slug])

  if (state === 'loading') return <PublicLoading />
  if (state === 'notFound' || !company) {
    return (
      <PublicNotFound
        title='Sayfa bulunamadı'
        description='Bu kariyer sayfası yayında değil ya da bağlantı hatalı olabilir.'
      />
    )
  }

  const logoUrl = publicDocumentUrl(company.logoDocumentId)
  const websiteUrl = safeExternalUrl(company.website)
  const linkedinUrl = safeExternalUrl(company.linkedinUrl)

  return (
    <div className='pc-page pb-20'>
      <PublicBanner headerDocumentId={company.headerDocumentId} />

      <div className='pc-container'>
        <div className='pc-company-head'>
          <div className='pc-logo'>
            {logoUrl ? (
              <img src={logoUrl} alt={company.name} />
            ) : (
              <span className='pc-logo-fallback'>{company.name.charAt(0).toUpperCase()}</span>
            )}
          </div>
          <div className='pt-lg-10 flex-grow-1'>
            <h1 className='pc-title'>{company.name}</h1>
            {company.tagline && <div className='pc-subtitle'>{company.tagline}</div>}
          </div>
        </div>

        <div className='row g-10 mt-4'>
          <div className='col-lg-7'>
            <div className='pc-meta mb-6'>
              {company.industry && (
                <span className='pc-meta-item'>
                  <i className='bi bi-buildings'></i>
                  {company.industry}
                </span>
              )}
              {company.employeeCount && (
                <span className='pc-meta-item'>
                  <i className='bi bi-people'></i>
                  {company.employeeCount} kişi
                </span>
              )}
              {company.country && (
                <span className='pc-pill'>
                  <i className='bi bi-geo-alt'></i>
                  {company.country}
                </span>
              )}
            </div>

            {(websiteUrl || linkedinUrl) && (
              <div className='pc-links mb-10'>
                {websiteUrl && (
                  <a href={websiteUrl} target='_blank' rel='noopener noreferrer' title='Web sitesi'>
                    <i className='bi bi-link-45deg'></i>
                  </a>
                )}
                {linkedinUrl && (
                  <a href={linkedinUrl} target='_blank' rel='noopener noreferrer' title='LinkedIn'>
                    <i className='bi bi-linkedin'></i>
                  </a>
                )}
              </div>
            )}

            {company.about && <div className='pc-about'>{company.about}</div>}
          </div>

          <div className='col-lg-5'>
            <h2 className='pc-section-title'>Açık Pozisyonlar</h2>
            {jobs.length === 0 ? (
              <div className='text-muted fs-5'>Şu anda açık pozisyon bulunmuyor.</div>
            ) : (
              jobs.map((job) => (
                <Link key={job.id} to={`${PUBLIC_JOB_PATH}/${job.id}`} className='pc-job-card'>
                  <div className='d-flex flex-wrap align-items-center gap-4 mb-4'>
                    <h3 className='pc-job-card-title'>{job.title}</h3>
                    {job.sector && (
                      <span className='pc-pill pc-pill--accent'>
                        {optionLabel(SECTOR_OPTIONS, job.sector)}
                      </span>
                    )}
                  </div>
                  {job.locationList.length > 0 && (
                    <div className='d-flex flex-wrap gap-2 mb-4'>
                      <JobLocations job={job} />
                    </div>
                  )}
                  <JobMeta job={job} showSector={false} showLocations={false} />
                </Link>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export {PublicCompanyPage}
